import { useEffect, useState } from 'react';

interface OrderCellProps {
  /** Current 1-based position. */
  position: number;
  /** How many items are in the ordered set — the highest position that can be typed. */
  total: number;
  disabled?: boolean;
  /** Shown as the tooltip when disabled, to explain why rather than just greying out. */
  disabledHint?: string;
  /** Called with the requested 1-based position. */
  onMove: (targetPosition: number) => void;
}

/**
 * Position control for an ordered list row: a number you can type plus up/down arrows.
 *
 * The typed value is committed on blur or Enter, never on each keystroke — typing "12" would
 * otherwise fire a move to position 1 first and shuffle the list under the cursor. Escape
 * abandons the edit.
 */
function OrderCell({ position, total, disabled, disabledHint, onMove }: OrderCellProps) {
  const [draft, setDraft] = useState(String(position));

  // Follow the row when the list is reordered from elsewhere (the other arrow, a refetch).
  useEffect(() => {
    setDraft(String(position));
  }, [position]);

  const commit = () => {
    const parsed = Number.parseInt(draft, 10);
    if (Number.isNaN(parsed) || parsed === position) {
      setDraft(String(position));
      return;
    }
    onMove(Math.min(Math.max(parsed, 1), total));
  };

  return (
    <div className="order-cell" title={disabled ? disabledHint : undefined}>
      <input
        className="order-cell-input"
        type="number"
        min={1}
        max={total}
        value={draft}
        disabled={disabled}
        aria-label={`Display position, currently ${position} of ${total}`}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            (e.target as HTMLInputElement).blur();
          } else if (e.key === 'Escape') {
            setDraft(String(position));
            (e.target as HTMLInputElement).blur();
          }
        }}
        onClick={(e) => e.stopPropagation()}
      />
      <div className="order-cell-arrows">
        <button
          type="button"
          className="order-cell-arrow"
          aria-label="Move up"
          disabled={disabled || position <= 1}
          onClick={(e) => { e.stopPropagation(); onMove(position - 1); }}
        >
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
            <path d="M2 6.5L5 3.5l3 3" />
          </svg>
        </button>
        <button
          type="button"
          className="order-cell-arrow"
          aria-label="Move down"
          disabled={disabled || position >= total}
          onClick={(e) => { e.stopPropagation(); onMove(position + 1); }}
        >
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
            <path d="M2 3.5L5 6.5l3-3" />
          </svg>
        </button>
      </div>
    </div>
  );
}

export default OrderCell;
